import { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, Brain, CalendarDays, FlaskConical, LayoutDashboard, Loader2, Pill, Ruler, Send, ShieldAlert, Stethoscope, Upload, UserRound, Users } from 'lucide-react';
import { TabBar } from '@/components/ui/TabBar';
import { Button } from '@/components/ui/button';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { EcgStudyDetailDialog } from '@/components/ecg/EcgStudyDetailDialog';
import { APPOINTMENT_DOCUMENT } from '@/lib/cabinet/appointments';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
    emptyRange,
    loadPatientCardData,
    type DateRange,
    type PatientCardData,
} from '@/components/medic/patientCardData';
import { PatientCardHeader } from '@/components/medic/PatientCardHeader';
import { AssignDoctorDialog } from '@/components/medic/AssignDoctorDialog';
import { ImportLabsDialog } from '@/components/medic/ImportLabsDialog';
import { PatientCardOverview, type PatientCardTab } from '@/components/medic/PatientCardOverview';
import { AMBULANCE_CALLS_TABLE } from '@/components/medic/patientAmbulanceData';
import { PatientCardEcgSection } from '@/components/medic/PatientCardEcgSection';
import { PatientCardPsySection } from '@/components/medic/PatientCardPsySection';
import { PatientCardAppointmentsSection } from '@/components/medic/PatientCardAppointmentsSection';
import { PatientCardRecordsSection } from '@/components/medic/PatientCardRecordsSection';
import {
    ALLERGIES_TABLE,
    CARE_TEAM_TABLE,
    DIAGNOSES_TABLE,
    LAB_RESULTS_TABLE,
    MEASUREMENTS_TABLE,
    PRESCRIPTIONS_TABLE,
    REFERRALS_TABLE,
    type PatientRecordCollection,
} from '@/components/medic/patientCardRecordsData';
import { CardState, InlineNotice } from '@/components/medic/PatientCardStates';
import { useAuthStore } from '@/store/authStore';

export interface PatientCardProps {
    patientId: string;
    patientName?: string;
    onOpenTests?: (patientId: string) => void;
    onOpenAppointment?: (appointmentId: string) => void;
}

type RecordDialog = {
    objectType: 'catalog' | 'document';
    objectName: string;
    objectId?: string;
    initialData?: Record<string, unknown>;
} | null;

function countOf(collection: PatientRecordCollection): number | null {
    return collection.state === 'ready' ? collection.items.length : null;
}

function tabLabel(label: string, count: number | null): string {
    return count === null ? label : `${label} (${count})`;
}

export function PatientCard({ patientId, patientName, onOpenTests, onOpenAppointment }: PatientCardProps) {
    const [data, setData] = useState<PatientCardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState<PatientCardTab>('overview');
    const [ecgRange, setEcgRange] = useState<DateRange>(emptyRange);
    const [psyRange, setPsyRange] = useState<DateRange>(emptyRange);
    const [appointmentRange, setAppointmentRange] = useState<DateRange>(emptyRange);
    const [studyId, setStudyId] = useState('');
    const [appointmentId, setAppointmentId] = useState('');
    const [recordDialog, setRecordDialog] = useState<RecordDialog>(null);
    const [assignDoctorOpen, setAssignDoctorOpen] = useState(false);
    const [importLabsOpen, setImportLabsOpen] = useState(false);
    const myId = useAuthStore((state) => state.user?.id || '');
    const sequence = useRef(0);

    const load = useCallback(async (force: boolean) => {
        const current = ++sequence.current;
        setLoading(true);
        setError('');
        try {
            const result = await loadPatientCardData(patientId, force);
            if (sequence.current !== current) return;
            setData(result);
            setError('');
        } catch (e) {
            if (sequence.current !== current) return;
            logErrorToJournal(e, `Карточка пациента ${patientId}: загрузка`);
            setError(friendlyError(e));
        } finally {
            if (sequence.current === current) setLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        setData(null);
        setError('');
        setTab('overview');
        setEcgRange(emptyRange());
        setPsyRange(emptyRange());
        setAppointmentRange(emptyRange());
        setStudyId('');
        setAppointmentId('');
        setRecordDialog(null);
        setAssignDoctorOpen(false);
        setImportLabsOpen(false);
        void load(false);
    }, [load]);

    useEffect(() => () => { sequence.current += 1; }, []);

    const reload = useCallback(() => {
        void load(true);
    }, [load]);

    const openAppointment = useCallback((id: string) => {
        if (!id) return;
        if (onOpenAppointment) {
            onOpenAppointment(id);
            return;
        }
        setAppointmentId(id);
    }, [onOpenAppointment]);

    if (!data && loading) {
        return (
            <CardState
                icon={<Loader2 className="h-6 w-6 animate-spin text-primary" />}
                title="Загрузка карточки пациента…"
                text={patientName ? `Пациент: ${patientName}` : undefined}
            />
        );
    }

    if (!data && error) {
        return (
            <CardState
                icon={<UserRound className="h-6 w-6 text-destructive" />}
                title="Не удалось открыть карточку пациента"
                text={error}
                tone="danger"
                onRetry={reload}
            />
        );
    }

    if (!data) {
        return (
            <CardState
                icon={<UserRound className="h-6 w-6 text-muted-foreground" />}
                title="Карточка пациента недоступна"
                text="Данные не загружены. Повторите попытку."
                onRetry={reload}
            />
        );
    }

    const ecgCount = data.ecg.state === 'ready' ? data.ecg.studies.length : null;
    const psyCount = data.psy.state === 'ready'
        ? data.psy.results.length + data.psy.conclusions.length
        : null;
    const appointmentCount = data.appointments.state === 'ready' ? data.appointments.items.length : null;
    const records = data.records;

    const tabs = [
        {
            id: 'overview',
            label: (<><LayoutDashboard className="mr-1 inline h-3 w-3" />Основное</>),
        },
        {
            id: 'appointments',
            label: (<><CalendarDays className="mr-1 inline h-3 w-3" />{tabLabel('Приёмы', appointmentCount)}</>),
        },
        {
            id: 'team',
            label: (<><Users className="mr-1 inline h-3 w-3" />{tabLabel('Команда', countOf(records.careTeam))}</>),
        },
        {
            id: 'labs',
            label: (<><FlaskConical className="mr-1 inline h-3 w-3" />{tabLabel('Анализы', countOf(records.labs))}</>),
        },
        {
            id: 'diagnoses',
            label: (<><Stethoscope className="mr-1 inline h-3 w-3" />{tabLabel('Диагнозы', countOf(records.diagnoses))}</>),
        },
        {
            id: 'prescriptions',
            label: (<><Pill className="mr-1 inline h-3 w-3" />{tabLabel('Назначения', countOf(records.prescriptions))}</>),
        },
        {
            id: 'allergies',
            label: (<><ShieldAlert className="mr-1 inline h-3 w-3" />{tabLabel('Аллергии', countOf(records.allergies))}</>),
        },
        {
            id: 'referrals',
            label: (<><Send className="mr-1 inline h-3 w-3" />{tabLabel('Направления', countOf(records.referrals))}</>),
        },
        {
            id: 'measurements',
            label: (<><Ruler className="mr-1 inline h-3 w-3" />{tabLabel('Измерения', countOf(records.measurements))}</>),
        },
        {
            id: 'ecg',
            label: (<><Activity className="mr-1 inline h-3 w-3" />{tabLabel('ЭКГ', ecgCount)}</>),
        },
        {
            id: 'psy',
            label: (<><Brain className="mr-1 inline h-3 w-3" />{tabLabel('Психология', psyCount)}</>),
        },
    ];

    return (
        <div className="flex h-full min-h-0 flex-col">
            <PatientCardHeader
                patient={data.patient}
                busy={loading}
                isMine={!!myId && data.patient.attendingDoctorId === myId}
                onReload={reload}
                onAssignDoctor={() => setAssignDoctorOpen(true)}
            />
            {error && (
                <div className="px-4 pt-2">
                    <InlineNotice text={`Не удалось обновить данные: ${error}`} tone="danger" onRetry={reload} />
                </div>
            )}
            <div className="shrink-0 border-b px-3 py-2">
                <TabBar
                    tabs={tabs}
                    active={tab}
                    onChange={(id) => setTab(id as PatientCardTab)}
                    group="medic-patient-card"
                />
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-4">
                {tab === 'overview' && (
                    <PatientCardOverview
                        data={data}
                        onGoTab={setTab}
                        onOpenStudy={setStudyId}
                        onOpenAppointment={openAppointment}
                        onOpenCall={(callId) => setRecordDialog({
                            objectType: 'document',
                            objectName: AMBULANCE_CALLS_TABLE,
                            objectId: callId,
                        })}
                    />
                )}
                {tab === 'ecg' && (
                    <PatientCardEcgSection
                        data={data.ecg}
                        range={ecgRange}
                        onRangeChange={setEcgRange}
                        onReload={reload}
                        onOpenStudy={setStudyId}
                    />
                )}
                {tab === 'psy' && (
                    <PatientCardPsySection
                        data={data.psy}
                        range={psyRange}
                        onRangeChange={setPsyRange}
                        onReload={reload}
                        onOpenTests={onOpenTests}
                        patientId={patientId}
                    />
                )}
                {tab === 'appointments' && (
                    <PatientCardAppointmentsSection
                        data={data.appointments}
                        range={appointmentRange}
                        onRangeChange={setAppointmentRange}
                        onReload={reload}
                        onOpenAppointment={openAppointment}
                    />
                )}
                {tab === 'team' && (
                    <PatientCardRecordsSection
                        title="Команда пациента"
                        description="Кто наблюдает пациента. Лечащего врача назначают кнопкой «Назначить» или «Передать» в шапке карточки."
                        collection={records.careTeam}
                        createLabel="Добавить в команду"
                        emptyTitle="Команда пока не заполнена"
                        emptyText="Назначьте лечащего врача в шапке карточки или добавьте консультанта."
                        onCreate={() => setRecordDialog({
                            objectType: 'catalog',
                            objectName: CARE_TEAM_TABLE,
                            initialData: {
                                patient_id: patientId,
                                role: 'consultant',
                                status: 'active',
                                started_at: new Date().toISOString().slice(0, 10),
                            },
                        })}
                        actions={(
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => setAssignDoctorOpen(true)}
                                title="Лечащий врач назначается командой: карточка и история меняются вместе"
                            >
                                <Stethoscope className="mr-1 h-3.5 w-3.5" />
                                {data.patient.attendingDoctorId ? 'Передать врача' : 'Назначить врача'}
                            </Button>
                        )}
                        onOpen={(id) => setRecordDialog({ objectType: 'catalog', objectName: CARE_TEAM_TABLE, objectId: id })}
                        onReload={reload}
                    />
                )}
                {tab === 'labs' && (
                    <PatientCardRecordsSection
                        title="Анализы"
                        description="Результаты лаборатории с показателями. Можно добавить вручную или импортировать CSV — исходный файл сохранится в карточке."
                        collection={records.labs}
                        createLabel="Добавить результат"
                        emptyTitle="Анализов пока нет"
                        emptyText="Добавьте результат вручную или импортируйте CSV из лаборатории."
                        onCreate={() => setRecordDialog({
                            objectType: 'document',
                            objectName: LAB_RESULTS_TABLE,
                            initialData: {
                                patient_id: patientId,
                                status: 'draft',
                                source: 'manual',
                                collected_at: new Date().toISOString().slice(0, 19),
                            },
                        })}
                        onOpen={(id) => setRecordDialog({ objectType: 'document', objectName: LAB_RESULTS_TABLE, objectId: id })}
                        onReload={reload}
                        actions={(
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => setImportLabsOpen(true)}
                                title="Импорт анализов из CSV: построчные ошибки, исходный файл сохраняется"
                            >
                                <Upload className="mr-1 h-3.5 w-3.5" /> Импорт CSV
                            </Button>
                        )}
                    />
                )}
                {tab === 'diagnoses' && (
                    <PatientCardRecordsSection
                        title="Диагнозы"
                        description="Основные и сопутствующие диагнозы с кодом МКБ-10. Снятые диагнозы остаются в истории."
                        collection={records.diagnoses}
                        createLabel="Добавить диагноз"
                        emptyTitle="Диагнозов пока нет"
                        emptyText="Добавьте основной или предварительный диагноз."
                        onCreate={() => setRecordDialog({
                            objectType: 'catalog',
                            objectName: DIAGNOSES_TABLE,
                            initialData: {
                                patient_id: patientId,
                                diagnosis_type: 'primary',
                                status: 'active',
                                onset_at: new Date().toISOString().slice(0, 10),
                            },
                        })}
                        onOpen={(id) => setRecordDialog({ objectType: 'catalog', objectName: DIAGNOSES_TABLE, objectId: id })}
                        onReload={reload}
                    />
                )}
                {tab === 'prescriptions' && (
                    <PatientCardRecordsSection
                        title="Назначения"
                        description="Лекарства и схема приёма. Активные назначения видны в сводке карточки."
                        collection={records.prescriptions}
                        createLabel="Добавить назначение"
                        emptyTitle="Назначений пока нет"
                        emptyText="Добавьте препарат, дозу и схему приёма."
                        onCreate={() => setRecordDialog({
                            objectType: 'catalog',
                            objectName: PRESCRIPTIONS_TABLE,
                            initialData: {
                                patient_id: patientId,
                                status: 'active',
                                start_at: new Date().toISOString().slice(0, 10),
                            },
                        })}
                        onOpen={(id) => setRecordDialog({ objectType: 'catalog', objectName: PRESCRIPTIONS_TABLE, objectId: id })}
                        onReload={reload}
                    />
                )}
                {tab === 'allergies' && (
                    <PatientCardRecordsSection
                        title="Аллергии"
                        description="Аллергены и реакции. Активные аллергии выделены в сводке карточки."
                        collection={records.allergies}
                        createLabel="Добавить аллергию"
                        emptyTitle="Аллергий не зафиксировано"
                        emptyText="Добавьте аллерген, если у пациента есть реакция на лекарства или продукты."
                        onCreate={() => setRecordDialog({
                            objectType: 'catalog',
                            objectName: ALLERGIES_TABLE,
                            initialData: {
                                patient_id: patientId,
                                severity: 'mild',
                                status: 'active',
                                recorded_at: new Date().toISOString().slice(0, 10),
                            },
                        })}
                        onOpen={(id) => setRecordDialog({ objectType: 'catalog', objectName: ALLERGIES_TABLE, objectId: id })}
                        onReload={reload}
                    />
                )}
                {tab === 'referrals' && (
                    <PatientCardRecordsSection
                        title="Направления"
                        description="Куда и к какому врачу направлен пациент. Направление остаётся в карточке после выполнения."
                        collection={records.referrals}
                        createLabel="Добавить направление"
                        emptyTitle="Направлений пока нет"
                        emptyText="Добавьте направление на лабораторию, исследование или консультацию."
                        onCreate={() => setRecordDialog({
                            objectType: 'document',
                            objectName: REFERRALS_TABLE,
                            initialData: {
                                patient_id: patientId,
                                referral_type: 'lab',
                                status: 'issued',
                            },
                        })}
                        onOpen={(id) => setRecordDialog({ objectType: 'document', objectName: REFERRALS_TABLE, objectId: id })}
                        onReload={reload}
                    />
                )}
                {tab === 'measurements' && (
                    <PatientCardRecordsSection
                        title="Измерения"
                        description="Температура, давление, пульс, сатурация, вес, рост и глюкоза."
                        collection={records.measurements}
                        createLabel="Добавить измерение"
                        emptyTitle="Измерений пока нет"
                        emptyText="Добавьте температуру, давление или другой показатель."
                        onCreate={() => setRecordDialog({
                            objectType: 'document',
                            objectName: MEASUREMENTS_TABLE,
                            initialData: {
                                patient_id: patientId,
                                measurement_type: 'blood_pressure',
                                source: 'manual',
                                measured_at: new Date().toISOString().slice(0, 19),
                            },
                        })}
                        onOpen={(id) => setRecordDialog({ objectType: 'document', objectName: MEASUREMENTS_TABLE, objectId: id })}
                        onReload={reload}
                    />
                )}
            </div>

            <EcgStudyDetailDialog
                studyId={studyId}
                onClose={() => setStudyId('')}
                onOpenPatient={() => setStudyId('')}
                onSaved={reload}
            />

            {appointmentId && !onOpenAppointment && (
                <AutoObjectForm
                    objectType="document"
                    objectName={APPOINTMENT_DOCUMENT}
                    objectId={appointmentId}
                    onClose={() => {
                        setAppointmentId('');
                        reload();
                    }}
                />
            )}

            {recordDialog && (
                <AutoObjectForm
                    objectType={recordDialog.objectType}
                    objectName={recordDialog.objectName}
                    objectId={recordDialog.objectId}
                    initialData={recordDialog.initialData}
                    onClose={() => {
                        setRecordDialog(null);
                        reload();
                    }}
                />
            )}

            {assignDoctorOpen && (
                <AssignDoctorDialog
                    patientId={patientId}
                    patientName={data.patient.name || patientName}
                    currentDoctorId={data.patient.attendingDoctorId}
                    onClose={() => setAssignDoctorOpen(false)}
                    onAssigned={reload}
                />
            )}

            {importLabsOpen && (
                <ImportLabsDialog
                    patientId={patientId}
                    patientName={data.patient.name || patientName}
                    onClose={() => setImportLabsOpen(false)}
                    onImported={reload}
                    onOpenResult={(documentId) => {
                        setImportLabsOpen(false);
                        setRecordDialog({ objectType: 'document', objectName: LAB_RESULTS_TABLE, objectId: documentId });
                    }}
                />
            )}
        </div>
    );
}
